<script setup lang="ts">
/**
 * Modal (Dialog) Component
 *
 * An accessible modal dialog with backdrop overlay, focus trapping,
 * scroll lock, and keyboard dismiss. Content is structured via
 * named slots: header, default (body), and footer.
 *
 * @example Basic usage
 * <UiModal :open="isOpen" aria-labelledby="title" @close="isOpen = false">
 *   <template #header>
 *     <h3 id="title">Add User</h3>
 *     <UiModalCloseButton @click="isOpen = false" />
 *   </template>
 *   <p>Modal body content</p>
 *   <template #footer>
 *     <UiButton @click="isOpen = false">Cancel</UiButton>
 *   </template>
 * </UiModal>
 *
 * @see https://www.w3.org/WAI/ARIA/apg/patterns/dialog-modal/
 */
import { computed, nextTick, onUnmounted, ref, useId, useTemplateRef, watch } from "vue";

import { useComponentClasses, useModalStack } from "../../composables";
import ModalFallbackHeader from "./ModalFallbackHeader.vue";

/**
 * Modal component props.
 */
export interface ModalProps {
  /** Accessible label for the dialog */
  ariaLabel?: string;
  /** ID of the element that labels the dialog */
  ariaLabelledby?: string;
  /** Description of the modal */
  description?: string;
  /** Whether the modal is open */
  open?: boolean;
  /** Class(es) applied to the panel element for sizing overrides */
  panelClass?: string;
  /** Prevents the modal from closing when the backdrop is clicked */
  preventBackdropClose?: boolean;
  /** Prevents the modal from closing when Escape is pressed */
  preventEscapeClose?: boolean;
  /** Title of the modal */
  title?: string;
}

const {
  ariaLabel,
  ariaLabelledby,
  open,
  panelClass,
  preventBackdropClose,
  preventEscapeClose,
  title,
} = defineProps<ModalProps>();

const emit = defineEmits<{
  /** Emitted when modal should close */
  (event: "close"): void;
}>();

defineSlots<{
  /** Main content area (scrollable) */
  default(): unknown;
  /** Optional footer region - typically action buttons */
  footer?(): unknown;
  /** Optional header region - typically title + ModalCloseButton */
  header?(): unknown;
}>();

const { element, root } = useComponentClasses("ui-modal");

const rootClass = root();

/** Used to generate a unique ID for the title element for the aria-labelledby attribute */
const titleId = useId();

const modalId = useId();
const { isTopmost, register, unregister, zIndex } = useModalStack(modalId);

const panelRef = useTemplateRef<HTMLElement>("panelRef");
const previouslyFocusedElement = ref<HTMLElement | null>(null);

const effectiveAriaLabelledby = computed(() => {
  if (ariaLabelledby) return ariaLabelledby;
  if (title) return titleId;

  return undefined;
});

function close() {
  emit("close");
}

function handleBackdropClick() {
  if (preventBackdropClose) return;
  if (isTopmost.value) {
    close();
  }
}

function handleKeydown(event: KeyboardEvent) {
  if (event.key === "Tab" && isTopmost.value) {
    trapFocus(event);
  }
}

/**
 * Keeps Tab / Shift+Tab cycling within the modal panel.
 *
 * Finds every focusable element inside the panel and, when the user
 * reaches an edge, wraps focus to the opposite end:
 *   - Tab on the last element  → moves focus to the first
 *   - Shift+Tab on the first  → moves focus to the last
 *
 * Without this, pressing Tab would escape into the page behind the
 * backdrop. aria-modal="true" handles this for screen readers, but
 * the keyboard needs an explicit trap.
 */
function trapFocus(event: KeyboardEvent) {
  const panel = panelRef.value;
  if (!panel) return;

  const focusableSelectors = [
    "a[href]",
    "button:not([disabled])",
    "input:not([disabled])",
    "select:not([disabled])",
    "textarea:not([disabled])",
    "[tabindex]:not([tabindex='-1'])",
  ].join(", ");

  const focusableElements = Array.from(panel.querySelectorAll<HTMLElement>(focusableSelectors));

  if (focusableElements.length === 0) return;

  const firstFocusable = focusableElements[0];
  const lastFocusable = focusableElements[focusableElements.length - 1];

  // Wrap forward: last element → first element
  if (!event.shiftKey && document.activeElement === lastFocusable) {
    event.preventDefault();
    firstFocusable.focus();
  }

  // Wrap backward: first element → last element
  if (event.shiftKey && document.activeElement === firstFocusable) {
    event.preventDefault();
    lastFocusable.focus();
  }
}

/**
 * Manages side effects when the modal opens and closes.
 * Uses { immediate: true } so effects apply if the modal starts open on mount.
 */
watch(
  () => open,
  async (isOpen) => {
    if (isOpen) {
      // Remember which element had focus so we can restore it on close
      previouslyFocusedElement.value = document.activeElement as HTMLElement;

      // Register with the modal stack (handles scroll lock and Escape key)
      register(() => {
        if (preventEscapeClose) return;
        close();
      });

      // Listen for Tab (focus trap) on the whole document
      document.addEventListener("keydown", handleKeydown);

      // Wait for the panel to render before moving focus into it
      await nextTick();

      // Move focus to the first focusable element inside the modal,
      // or the panel itself if there are no interactive elements
      const panel = panelRef.value;
      if (panel) {
        const firstFocusable = panel.querySelector<HTMLElement>(
          "a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])"
        );
        if (firstFocusable) {
          firstFocusable.focus();
        } else {
          panel.focus();
        }
      }
    } else {
      // Unregister from the modal stack (handles scroll lock cleanup)
      unregister();
      document.removeEventListener("keydown", handleKeydown);

      // Return focus to the element that opened the modal
      previouslyFocusedElement.value?.focus();
      previouslyFocusedElement.value = null;
    }
  },
  { immediate: true }
);

onUnmounted(() => {
  if (open) {
    unregister();
    document.removeEventListener("keydown", handleKeydown);
  }
});
</script>

<template>
  <Teleport to="body">
    <Transition name="ui-modal" :duration="500" appear>
      <!-- Critical to unmount modal content when the modal is closed to ensure things like form stae is reset -->
      <div v-if="open" :class="rootClass">
        <div
          :class="element('backdrop')"
          :style="{ zIndex }"
          aria-hidden="true"
          @click="handleBackdropClick"
        />

        <div :class="element('container')" :style="{ zIndex }" @click.self="handleBackdropClick">
          <div
            ref="panelRef"
            :class="[element('panel'), panelClass]"
            role="dialog"
            aria-modal="true"
            :aria-label="ariaLabel"
            :aria-labelledby="effectiveAriaLabelledby"
            tabindex="-1"
          >
            <template v-if="$slots.header">
              <slot name="header" />
            </template>
            <ModalFallbackHeader
              v-else-if="title"
              :title
              :title-id="effectiveAriaLabelledby"
              :description
              @close="close"
            />

            <slot />

            <template v-if="$slots.footer">
              <slot name="footer" />
            </template>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.ui-modal-enter-active .ui-modal__backdrop,
.ui-modal-leave-active .ui-modal__backdrop {
  transition:
    background-color 500ms ease-in-out,
    backdrop-filter 500ms ease-in-out,
    -webkit-backdrop-filter 500ms ease-in-out;
}

.ui-modal-enter-from .ui-modal__backdrop,
.ui-modal-leave-to .ui-modal__backdrop {
  background-color: transparent;
  backdrop-filter: blur(0px);
  -webkit-backdrop-filter: blur(0px);
}

.ui-modal-enter-active .ui-modal__container,
.ui-modal-leave-active .ui-modal__container {
  transition:
    opacity 200ms ease-in-out,
    transform 200ms ease-in-out;
}

.ui-modal-enter-from .ui-modal__container,
.ui-modal-leave-to .ui-modal__container {
  opacity: 0;
  transform: translateY(5rem);
}

.ui-modal {
  --_padding-y: var(--ui-modal-padding-y, 1.25rem);
  --_padding-x: var(--ui-modal-padding-x, 1.5rem);
}

.ui-modal__backdrop {
  position: fixed;
  inset: 0;
  background-color: var(--ui-modal-color-backdrop, rgba(0, 0, 0, 0.5));
  z-index: 50;
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
}

.ui-modal__container {
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow-y: auto;
  padding: 1rem;
}

.ui-modal__panel {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: var(--ui-modal-panel-max-width, 900px);
  max-height: calc(100vh - 2rem);
  background-color: var(--ui-modal-panel-color-bg, var(--ui-color-white));
  border-radius: var(--ui-modal-panel-radius, var(--ui-radius-lg));
  box-shadow: var(
    --ui-modal-panel-shadow,
    0px 20px 24px -4px rgb(0 0 0 / 0.1),
    0px 8px 8px -4px rgb(0 0 0 / 0.1)
  );
  overflow: hidden;
}

.ui-modal__panel:focus {
  outline: none;
}
</style>
